import { useEffect, useState, useCallback } from "react";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddPhoto from "./../../../assets/add-photo.png";
import AddMore from "./../../../assets/add-more.svg";
import Delete from "./../../../assets/delete.svg";
import AddIcon from "@mui/icons-material/AddBoxOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../../utils/api";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const ControlGroup = ({ isMobile, onDataChange, onHasDiscountChange, variationsHaveDiscount }) => {
  const type = localStorage.getItem("type");
  const { userData } = useSelector((state) => state.auth);
  const user = userData?.user || userData; // Handle different structure possibilities
  const [images, setImages] = useState([]);
  const [inputs, setInputs] = useState([{ id: 0, value: { ar: "", en: "" } }]);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [units, setUnits] = useState([]);
  const [product, setProduct] = useState({
    name: { ar: "", en: "" },
    price: "",
    description: { ar: "", en: "" },
    stock: "",
    unit_type: "",
    category_id: "",
    mid_category_id: "",
    sub_category_id: "",
    discount: "",
    discount_date: "",
    discount_date_to: "",
    min_quantity: "",
    max_quantity: "",
    vendor_id: user?.id || "",
    gallery: [],
    // Factory-specific fields
    check_samples: 0,
    allow_negotiation: 0,
    sample_qty: "",
    sample_price: "",
  });

  // Vendor options
  const [vendors, setVednors] = useState([]);
  const [vendor, setVednor] = useState("");

  // Category options
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [innerCategories, setInnerCategories] = useState([]);

  // Loading states
  const [loadingMainCategories, setLoadingMainCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingInnerCategories, setLoadingInnerCategories] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Override browser validation messages
  useEffect(() => {
    // Function to set custom validation messages
    const setupCustomValidationMessages = () => {
      const formElements = document.querySelectorAll("input, select, textarea");

      formElements.forEach((element) => {
        element.oninvalid = function (e) {
          e.target.setCustomValidity(""); // Clear any existing message
          if (!e.target.validity.valid) {
            e.target.setCustomValidity("هذا الحقل مطلوب");
          }
        };

        // Reset the custom validity when user starts typing
        element.oninput = function (e) {
          e.target.setCustomValidity("");
        };
      });
    };

    // Call the function to set up validation messages
    setupCustomValidationMessages();
  }, []);

  // Prepare form data without validation
  const prepareFormData = useCallback(() => {
    // Prepare form data to be sent to parent
    const formData = new FormData();
    // Add text fields
    formData.append("name[ar]", product.name.ar);
    formData.append("name[en]", product.name.en);
    formData.append("price", product.price);
    formData.append("stock", product.stock);
    formData.append("unit_type", product.unit_type);
    formData.append("category_id", product.category_id);
    formData.append("mid_category_id", product.mid_category_id);
    formData.append("sub_category_id", product.sub_category_id);
    formData.append("min_quantity", product.min_quantity);
    formData.append("max_quantity", product.max_quantity);
    formData.append("description[ar]", product.description.ar);
    formData.append("description[en]", product.description.en);
    formData.append("type", type);
    if (type !== "sahm") {
      formData.append("vendor_id", product.vendor_id);
    }

    // Add factory-specific sample fields
    if (type === "factory") {
      formData.append("check_samples", product.check_samples);
      if (product.check_samples === 1) {
        formData.append("sample_qty", product.sample_qty);
        formData.append("sample_price", product.sample_price);
      }

      if (product.allow_negotiation === 1) {
        formData.append("allow_negotiation", product.allow_negotiation);
      }
    }

    if (type !== "factory") {
      formData.append("check_samples", 0);
    }

    // Add discount fields if applicable
    if (hasDiscount) {
      formData.append("discount", product.discount);
      formData.append("discount_date", product.discount_date);
      formData.append("discount_date_to", product.discount_date_to);
    }

    // Add newly uploaded image files
    images.forEach((image, index) => {
      formData.append(`gallery[${index}]`, image);
    });

    return formData;
  }, [product, hasDiscount, images, type]);

  // Send data to parent whenever form data changes
  useEffect(() => {
    if (onDataChange) {
      const formData = prepareFormData();
      onDataChange(formData);
    }
  }, [product, hasDiscount, images, onDataChange, prepareFormData]);

  // Fetch vendors on component mount
  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const params = new URLSearchParams({
          type: type, // Get vendors of the specified type
        });

        const response = await api.get(`/vendors?${params.toString()}`);
        setVednors(response.data.data || []);
      } catch (error) {
        console.error("Error fetching main vendors", error);
      } finally {
        setLoadingVendors(false);
      }
    };
    if (type !== "sahm") {
      fetchVendors();
    }
  }, [type]);

  // Fetch main categories on component mount
  useEffect(() => {
    const fetchMainCategories = async () => {
      setLoadingMainCategories(true);
      try {
        const params = new URLSearchParams({
          flag: 1, // Get only main categories (flag 1)
          active: 1, // Get only active categories
          type: type === "sahm" ? "" : type, // Get categories of the specified type
          per_page: 100,
        });
        const response = await api.get(`/categories?${params.toString()}`);
        setMainCategories(response.data.data || []);
      } catch (error) {
        console.error("Error fetching main categories:", error);
      } finally {
        setLoadingMainCategories(false);
      }
    };

    const fetchUnits = async () => {
      try {
        const response = await api.get("/variations?type=unit");
        setUnits(response.data.data || []);
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchUnits();
    fetchMainCategories();
  }, [type]);

  // Fetch Sub categories when main category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!product.category_id) return;

      setLoadingSubCategories(true);
      try {
        const params = new URLSearchParams({
          flag: 2, // Get Sub categories (flag 2)
          parent_id: product.category_id,
          active: 1, // Get only active categories
        });
        const response = await api.get(`/categories?${params.toString()}`);
        const subCategoriesData = response.data.data || [];
        setSubCategories(subCategoriesData);
      } catch (error) {
        console.error("Error fetching sub categories:", error);
      } finally {
        setLoadingSubCategories(false);
      }
    };

    if (product.category_id) {
      fetchSubCategories();
    }
  }, [product.category_id]);

  // Fetch inner categories when sub category changes
  useEffect(() => {
    const fetchInnerCategories = async () => {
      if (!product.mid_category_id) return;

      setLoadingInnerCategories(true);
      try {
        const params = new URLSearchParams({
          flag: 3, // Get inner categories (flag 3)
          active: 1, // Get only active
          sub_parent_id: product.mid_category_id,
        });
        const response = await api.get(`/categories?${params.toString()}`);
        const innerCategoriesData = response.data.data || [];
        setInnerCategories(innerCategoriesData);
      } catch (error) {
        console.error("Error fetching inner categories:", error);
      } finally {
        setLoadingInnerCategories(false);
      }
    };

    if (product.mid_category_id) {
      fetchInnerCategories();
    }
  }, [product.mid_category_id]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setImages((prevImages) => [...prevImages, ...newImages]);

    // Update the product state with the new gallery
    const updatedGallery = [...product.gallery, ...files];
    setProduct({
      ...product,
      gallery: updatedGallery,
    });
  };

  const handleDeleteImage = (indexToDelete) => {
    const updatedImages = images.filter((_, index) => index !== indexToDelete);

    // Revoke the URL to prevent memory leaks
    URL.revokeObjectURL(images[indexToDelete].preview);

    setImages(updatedImages);

    // Update the product state with the filtered gallery
    setProduct({
      ...product,
      gallery: updatedImages,
    });
  };

  const addInput = () => {
    const newId =
      inputs.length > 0 ? Math.max(...inputs.map((i) => i.id)) + 1 : 0;
    setInputs([...inputs, { id: newId, value: { ar: "", en: "" } }]);
  };

  const deleteInput = (id) => {
    setInputs(inputs.filter((input) => input.id !== id));

    // Update product description after removing an input
    const updatedInputs = inputs.filter((input) => input.id !== id);

    // Rebuild complete description string after deletion
    const descriptionAr = updatedInputs
      .map((input) => input.value.ar)
      .filter(Boolean)
      .join("\n");

    const descriptionEn = updatedInputs
      .map((input) => input.value.en)
      .filter(Boolean)
      .join("\n");

    // Update the product description
    setProduct({
      ...product,
      description: {
        ar: descriptionAr,
        en: descriptionEn,
      },
    });
  };

  const handleInputChange = (id, lang, value) => {
    // Update the specific input first
    setInputs(
      inputs.map((input) =>
        input.id === id
          ? { ...input, value: { ...input.value, [lang]: value } }
          : input
      )
    );

    // Create updated inputs array with the new value
    const updatedInputs = inputs.map((input) =>
      input.id === id
        ? { ...input, value: { ...input.value, [lang]: value } }
        : input
    );

    // Now build description strings properly for both languages
    const descriptionAr = updatedInputs
      .map((input) => input.value.ar)
      .filter(Boolean)
      .join("\n");

    const descriptionEn = updatedInputs
      .map((input) => input.value.en)
      .filter(Boolean)
      .join("\n");

    // Update the product description
    setProduct({
      ...product,
      description: {
        ar: descriptionAr,
        en: descriptionEn,
      },
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle nested fields with bracket notation (e.g., "name[ar]")
    if (name.includes("[") && name.includes("]")) {
      const mainField = name.split("[")[0];
      const nestedField = name.split("[")[1].split("]")[0];

      setProduct({
        ...product,
        [mainField]: {
          ...product[mainField],
          [nestedField]: value,
        },
      });
    } else {
      // Handle regular fields
      setProduct({
        ...product,
        [name]: value,
      });
    }
  };

  const handleDateChange = (name, date) => {
    // Format date as ISO string or use your preferred format
    const formattedDate = date ? date.format("YYYY-MM-DD") : "";

    setProduct({
      ...product,
      [name]: formattedDate,
    });
  };

  // Handle check_samples change
  const handleCheckSamplesChange = (e) => {
    const isChecked = e.target.checked;
    setProduct({
      ...product,
      check_samples: isChecked ? 1 : 0,
    });
  };

  const handleAllowNegotiationChange = (e) => {
    const isChecked = e.target.checked;
    setProduct({
      ...product,
      allow_negotiation: isChecked ? 1 : 0,
    });
  };

  // Clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      images.forEach((image) => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  return (
    <div className="control-group">
      <FormControl
        sx={{ width: isMobile ? "100%" : "647px" }}
        variant="outlined"
        required
      >
        <InputLabel
          htmlFor="product-name-ar"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          اسم المنتج بالعربية
        </InputLabel>
        <OutlinedInput
          sx={{ borderRadius: "8px" }}
          id="product-name-ar"
          type="text"
          notched
          name="name[ar]"
          value={product.name.ar}
          onChange={handleChange}
          required
        />
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "647px" }}
        variant="outlined"
        required
      >
        <InputLabel
          htmlFor="product-name-en"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          اسم المنتج بالانجليزية
        </InputLabel>
        <OutlinedInput
          sx={{ borderRadius: "8px" }}
          id="product-name-en"
          type="text"
          notched
          name="name[en]"
          value={product.name.en}
          onChange={handleChange}
          required
        />
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "316px" }}
        variant="outlined"
        required
      >
        <InputLabel
          htmlFor="product-price"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          سعر المنتج
        </InputLabel>
        <OutlinedInput
          sx={{ borderRadius: "8px" }}
          id="product-price"
          type="text"
          name="price"
          value={product.price}
          onChange={handleChange}
          notched
          required
        />
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "316px" }}
        variant="outlined"
        required
      >
        <InputLabel
          htmlFor="product-stock"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          الكمية
        </InputLabel>
        <OutlinedInput
          sx={{ borderRadius: "8px" }}
          id="product-stock"
          type="text"
          notched
          name="stock"
          value={product.stock}
          onChange={handleChange}
          required
        />
      </FormControl>

      {/* Quantity Selection */}
      <FormControl
        sx={{
          width: isMobile ? "100%" : "316px",
        }}
        required
      >
        <InputLabel
          htmlFor="unit-type"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.75)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          وحدة الكمية
        </InputLabel>
        <Select
          labelId="unit-type-label"
          id="unit-type"
          displayEmpty={true}
          sx={{ borderRadius: "8px" }}
          value={product.unit_type}
          name="unit_type"
          onChange={handleChange}
          required
        >
          <MenuItem value="">اختر الوحدة</MenuItem>
          {units.map((unit) => (
            <MenuItem key={unit.id} value={unit.id}>
              {unit.name_ar || ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "316px" }}
        variant="outlined"
      >
        <InputLabel
          htmlFor="min-quantity"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          الحد الادني للشراء
        </InputLabel>
        <OutlinedInput
          sx={{ borderRadius: "8px" }}
          id="min-quantity"
          type="text"
          notched
          name="min_quantity"
          value={product.min_quantity}
          onChange={handleChange}
        />
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "316px" }}
        variant="outlined"
      >
        <InputLabel
          htmlFor="max-quantity"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          الحد الاقصي للشراء
        </InputLabel>
        <OutlinedInput
          sx={{ borderRadius: "8px" }}
          id="max-quantity"
          type="text"
          notched
          name="max_quantity"
          value={product.max_quantity}
          onChange={handleChange}
        />
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "316px" }}
        variant="outlined"
        required
      >
        <InputLabel
          htmlFor="main-category"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          القسم الرئيسي
        </InputLabel>
        <Select
          labelId="category-id-label"
          id="category-id"
          displayEmpty={true}
          sx={{ borderRadius: "8px" }}
          disabled={loadingMainCategories}
          value={product.category_id || ""}
          name="category_id"
          onChange={handleChange}
          defaultValue=""
        >
          <MenuItem value="">
            {loadingMainCategories ? "جاري التحميل..." : "اختر القسم"}
          </MenuItem>
          {mainCategories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.title_ar || category.title?.ar || ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "316px" }}
        variant="outlined"
      >
        <InputLabel
          htmlFor="main-category"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          القسم الفرعي
        </InputLabel>
        <Select
          labelId="mid_category_id-label"
          id="mid_category_id"
          displayEmpty={true}
          sx={{ borderRadius: "8px" }}
          disabled={loadingMainCategories || !product.category_id}
          value={product.mid_category_id || ""}
          name="mid_category_id"
          onChange={handleChange}
        >
          <MenuItem value="">
            {loadingSubCategories ? "جاري التحميل..." : "اختر القسم"}
          </MenuItem>
          {subCategories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.title_ar || category.title?.ar || ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        sx={{ width: isMobile ? "100%" : "316px" }}
        variant="outlined"
      >
        <InputLabel
          htmlFor="inner-category"
          shrink
          sx={{
            background: "white",
            transformOrigin: "right top",
            transform: "translate(14px, -9px) scale(0.8)",
            right: 24,
            left: "auto",
            paddingLeft: "4px",
            paddingRight: "4px",
          }}
        >
          القسم الفرعي-2
        </InputLabel>
        <Select
          labelId="sub-category-id-label"
          id="sub-category-id"
          displayEmpty={true}
          sx={{ borderRadius: "8px" }}
          disabled={loadingInnerCategories || !product.mid_category_id}
          value={product.sub_category_id || ""}
          name="sub_category_id"
          onChange={handleChange}
        >
          <MenuItem value="">
            {loadingInnerCategories ? "جاري التحميل..." : "اختر القسم"}
          </MenuItem>
          {innerCategories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.title_ar || category.title?.ar || ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Vendor Selection */}
      {type !== "sahm" && (
        <FormControl
          sx={{ width: isMobile ? "100%" : "316px" }}
          variant="outlined"
        >
          <InputLabel
            htmlFor="inner-category"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.8)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            {type === "shop" ? "اسم المتجر" : "اسم المصنع"}
          </InputLabel>
          <Autocomplete
            options={vendors}
            getOptionLabel={(option) => option.name || ""}
            value={vendors.find((v) => v.id === vendor) || null}
            onChange={(event, newValue) => {
              const vendorId = newValue ? newValue.id : "";
              setVednor(vendorId);
              setProduct({
                ...product,
                vendor_id: vendorId,
              });
            }}
            loading={loadingVendors}
            disabled={loadingVendors}
            noOptionsText="لا توجد خيارات"
            loadingText="جاري التحميل..."
            sx={{ width: isMobile ? "100%" : "316px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                placeholder={
                  loadingVendors ? "جاري التحميل..." : "البحث واختيار الاسم"
                }
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.8)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "47px",
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
          />
        </FormControl>
      )}

      <div className="item">
        <h4>وصف المنتج</h4>
      </div>

      <div className="description-container">
        {inputs.map((input) => (
          <div key={input.id} className="desc-item">
            <FormControl
              sx={{ width: "100%", flex: "1" }}
              variant="outlined"
              required
            >
              <InputLabel
                htmlFor={`product-desc-ar-${input.id}`}
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                وصف المنتج بالعربية
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                id={`product-desc-ar-${input.id}`}
                type="text"
                notched
                value={input.value.ar}
                onChange={(e) =>
                  handleInputChange(input.id, "ar", e.target.value)
                }
                required={input.id === 0}
              />
            </FormControl>
            <FormControl
              sx={{ width: "100%", flex: "1" }}
              variant="outlined"
              required
            >
              <InputLabel
                htmlFor={`product-desc-en-${input.id}`}
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                وصف المنتج بالانجليزية
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                id={`product-desc-en-${input.id}`}
                type="text"
                notched
                value={input.value.en}
                onChange={(e) =>
                  handleInputChange(input.id, "en", e.target.value)
                }
                required={input.id === 0}
              />
            </FormControl>

            {isMobile ? (
              <AddIcon className="icon" onClick={addInput} />
            ) : (
              <img src={AddMore} alt="" onClick={addInput} />
            )}
            {input.id !== 0 && (
              <img src={Delete} alt="" onClick={() => deleteInput(input.id)} />
            )}
          </div>
        ))}
      </div>

      {/* Product Images Section */}
      <div className="product-form-img">
        {/* Display newly uploaded images */}
        <div
          className="imgs-container"
          style={{ display: images.length === 0 && "none" }}
        >
          {images.map((image, index) => (
            <div key={index} className="image-wrapper">
              <img src={image.preview} alt="" />
              <button
                className="delete-btn"
                onClick={() => handleDeleteImage(index)}
                type="button"
              >
                <DeleteIcon />
              </button>
            </div>
          ))}
        </div>

        <label htmlFor="product-images" className="img-control">
          <img src={AddPhoto} alt="" />
          <span>اضافة صور</span>
        </label>
        <input
          type="file"
          id="product-images"
          accept="image/*"
          multiple
          onChange={handleImageChange}
        />
      </div>

      {/* Discount Section */}
      {!variationsHaveDiscount && (
        <div className="item">
          <input
            type="checkbox"
            checked={hasDiscount}
            onChange={(e) => {
              setHasDiscount(e.target.checked);
              if (onHasDiscountChange) onHasDiscountChange(e.target.checked);
            }}
          />
          <label>يوجد خصم علي المنتج</label>
        </div>
      )}

      {hasDiscount && (
        <>
          <FormControl
            sx={{ width: isMobile ? "100%" : "316px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="discount"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.8)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              الخصم
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="discount"
              type="text"
              notched
              name="discount"
              value={product.discount}
              onChange={handleChange}
              endAdornment={<span style={{ marginLeft: "8px" }}>%</span>}
            />
          </FormControl>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "316px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: "row",
              borderRadius: "8px",
            }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="discount-date-from"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              التاريخ من
            </InputLabel>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              sx={{
                p: 0,
                width: "316px",
              }}
            >
              <DemoContainer
                components={["DatePicker"]}
                sx={{
                  p: 0,
                  width: "316px",
                  borderRadius: "8px",
                }}
              >
                <DatePicker
                  sx={{ width: "100%" }}
                  value={
                    product.discount_date ? dayjs(product.discount_date) : null
                  }
                  onChange={(date) => handleDateChange("discount_date", date)}
                />
              </DemoContainer>
            </LocalizationProvider>
          </FormControl>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "316px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: "row",
            }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="discount-date-to"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              التاريخ الي
            </InputLabel>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              sx={{
                p: 0,
                width: "316px",
              }}
            >
              <DemoContainer
                components={["DatePicker"]}
                sx={{
                  p: 0,
                  width: "316px",
                }}
              >
                <DatePicker
                  sx={{ width: "100%" }}
                  value={
                    product.discount_date_to
                      ? dayjs(product.discount_date_to)
                      : null
                  }
                  onChange={(date) =>
                    handleDateChange("discount_date_to", date)
                  }
                />
              </DemoContainer>
            </LocalizationProvider>
          </FormControl>
        </>
      )}

      {/* Factory Sample Section */}
      {type === "factory" && (
        <>
          <div className="item">
            <input
              type="checkbox"
              checked={product.allow_negotiation === 1}
              onChange={handleAllowNegotiationChange}
            />
            <label>يوجد تفاوض علي السعر</label>
          </div>
          <div className="item">
            <input
              type="checkbox"
              checked={product.check_samples === 1}
              onChange={handleCheckSamplesChange}
            />
            <label>يوجد عينات للمنتج</label>
          </div>

          {product.check_samples === 1 && (
            <>
              <FormControl
                sx={{ width: isMobile ? "100%" : "316px" }}
                variant="outlined"
                required
              >
                <InputLabel
                  htmlFor="sample-price"
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.8)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  سعر العينة
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  id="sample-price"
                  type="text"
                  notched
                  name="sample_price"
                  value={product.sample_price}
                  onChange={handleChange}
                  required
                />
              </FormControl>
              <FormControl
                sx={{ width: isMobile ? "100%" : "316px" }}
                variant="outlined"
                required
              >
                <InputLabel
                  htmlFor="sample-qty"
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.8)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  الحد الاقصي للعينة
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  id="sample-qty"
                  type="text"
                  notched
                  name="sample_qty"
                  value={product.sample_qty}
                  onChange={handleChange}
                  required
                />
              </FormControl>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ControlGroup;
